import { CompanyType, ObjectType, OrderIssueResolutionType } from '@planning/app/api'
import { FieldboxWithRadio } from '@planning/components/atoms/FieldboxWithRadio'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { FieldboxSelectionGroupController } from '@planning/components/molecules/FieldboxSelectionGroup'
import companyService from '@planning/services/companyService'
import { useTranslate } from '@tolgee/react'
import { FormField, IFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { IssueResolutionViewStore } from '../../Stores/IssueResolutionViewStore'
import { ResolveIssuesFormData } from '../templates/ResolveIssueDialog'

type Props = {
  controller: FieldboxSelectionGroupController
  formStore: IFormStore<ResolveIssuesFormData>
  issueResolutionStore: IssueResolutionViewStore
}

export const UpdateCompanyFieldbox: FC<Props> = observer(props => {
  const { controller, formStore, issueResolutionStore } = props
  const { t } = useTranslate()
  const [isPolling, setIsPolling] = useState(false)

  const issue = issueResolutionStore.issueToBeResolved!.data

  if (!issue.companyType) return <></>

  const isPropertyString =
    issue.companyType === CompanyType.Consignee ||
    (issue.companyType === CompanyType.ShippingLine && issue.objectType === ObjectType.Container)

  const companyTypeConfigMap: Partial<
    Record<
      CompanyType,
      {
        formKey: keyof ResolveIssuesFormData
        translationKey: string
        selectionKey: OrderIssueResolutionType
      }
    >
  > = {
    [CompanyType.Consignee]: {
      formKey: 'consignee',
      translationKey: 'consignee',
      selectionKey: OrderIssueResolutionType.UpdateConsignee,
    },
    [CompanyType.ShippingLine]:
      issue.objectType === ObjectType.Container
        ? {
            formKey: 'operator',
            translationKey: 'operator',
            selectionKey: OrderIssueResolutionType.UpdateOperator,
          }
        : {
            formKey: 'customerId',
            translationKey: 'customer',
            selectionKey: OrderIssueResolutionType.UpdateCustomer,
          },
    [CompanyType.Forwarder]: {
      formKey: 'forwarderId',
      translationKey: 'forwarder',
      selectionKey: OrderIssueResolutionType.UpdateForwarder,
    },
    [CompanyType.Shipper]: {
      formKey: 'shipperId',
      translationKey: 'shipper',
      selectionKey: OrderIssueResolutionType.UpdateShipper,
    },
  }

  const config = companyTypeConfigMap[issue.companyType]
  const companyTypes = [issue.companyType]

  if (!config) return <></>
  const { formKey, translationKey, selectionKey } = config

  const handleAddNewCompany = async (company: any, formFieldStore: any) => {
    try {
      setIsPolling(true)

      if (company && company.shortName) {
        let companyData

        for (let i = 0; i < 5; i++) {
          try {
            companyData = await companyService.getByShortName(company.shortName)
            if (companyData) {
              break
            }
          } catch (error: any) {
            if (error.response?.status !== 404) {
              throw error
            }
          }
          await new Promise(res => setTimeout(res, 500))
        }

        if (companyData) {
          isPropertyString
            ? formFieldStore.setValue(companyData.shortName ?? '')
            : formFieldStore.setValue(companyData.id ?? 0)
        } else {
          throw new Error(`Company '${company.shortName}' not found.`)
        }
      }
    } catch (error) {
      console.error(error)
      // appViewStore.setShowAlert('error', error.message)
    } finally {
      setIsPolling(false)
      issueResolutionStore.companyDialogUIStore.reset()
    }
  }

  return (
    <FieldboxWithRadio
      selectionKey={selectionKey}
      controller={controller}
      label={t(`update${translationKey}`)}
    >
      <FormField formStore={formStore} name={formKey} initialValue={isPropertyString ? '' : 0}>
        {store => {
          return (
            <CompanyAutocomplete
              label={t(translationKey)}
              filterByTypes={companyTypes}
              selectedCompanyShortName={isPropertyString ? (store.data as string) : undefined}
              selectedCompanyId={isPropertyString ? undefined : (store.data as number)}
              loading={isPolling}
              onAddNew={(e, inputValue) => {
                e?.preventDefault()
                e?.stopPropagation()
                issueResolutionStore.companyDialogUIStore.setSkipCancellableRequest(true)
                issueResolutionStore.companyDialogUIStore.openWithDefaultData(
                  {
                    shortName: inputValue,
                    companyTypes: [issueResolutionStore.issueToBeResolved!.data.companyType!],
                    name: inputValue,
                    alternativeNames: [''],
                  },
                  async (company: any) => {
                    await handleAddNewCompany(company, store)
                  },
                )
              }}
              handleOnChange={company => {
                isPropertyString
                  ? store.setValue(company?.shortName ?? '')
                  : store.setValue(company?.id ?? 0)
              }}
            />
          )
        }}
      </FormField>
    </FieldboxWithRadio>
  )
})
